class RegisterProductForm extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.skuAndSerial = this.querySelector('#skuAndSerial');
    this.penSelection = this.querySelector('#penSelection');
    this.productTypeRadios = this.querySelectorAll('input[name="productType"]');
    this.form = this.querySelector('#contactForm');
    this.submitButton = this.querySelector('button[type="submit"]');

    if (!this.form) return;

    this.googleMapsApiKey = this.dataset.mapsKey;
    this.apiUrl = this.dataset.apiUrl || "https://gevril.gadget.app/warranty";
    this.successUrl = this.dataset.successUrl || "/pages/thank-you-for-registering-your-watch";
    this.shopId = this.dataset.shopId;
    this.successMessage = this.dataset.successMessage || 'Registration successful! Redirecting...';
    this.errorMessage = this.dataset.errorMessage || 'Failed to send the registration. Please try again. If the issue persists, please contact us at support@gevril.com';

    this.googleMapsLoaded = false;
    this.autocompleteService = null;
    this.activeSuggestions = null;
    this.activeInput = null;
    this.errorMessage = null;
    this.lastLocality = null;

    this.init();
  }

  init() {
    this.bindEvents();
    if (this.googleMapsApiKey) {
      this.loadGoogleMapsScript(() => this.initializeAutocomplete());
    }
  }

  static mapGender(genderValue) {
    if (!genderValue) return '';
    if (genderValue === 'Mr.') return 'Male';
    if (genderValue === 'Mrs.' || genderValue === 'Ms.') return 'Female';
    return '';
  }

  static mapAverageIncome(incomeValue) {
    if (!incomeValue) return '';
    const mapping = {
      '0-50,000 annually': '0-50000',
      '50,000-100,000 annually': '75000-100000',
      '100,000-500,000 annually': '100000-500000',
      '500,000-1,000,000 annually': '500000-1000000',
      '1,000,000+ annually': '1000000+',
    };
    return mapping[incomeValue] || '';
  }

  bindEvents() {
    // Product Type Selection
    this.productTypeRadios.forEach((radio) => {
      radio.addEventListener('change', (e) => {
        if (e.target.value === 'pen') {
          if (this.skuAndSerial) this.skuAndSerial.style.display = 'none';
          if (this.penSelection) this.penSelection.style.display = 'block';
          if (this.skuAndSerial) this.skuAndSerial.querySelector('input[name="Item_SKU_Model_Number"]').required = false;
        } else {
          if (this.skuAndSerial) this.skuAndSerial.style.display = 'grid';
          if (this.penSelection) this.penSelection.style.display = 'none';
          if (this.skuAndSerial) {
            this.skuAndSerial.querySelector('input[name="Item_SKU_Model_Number"]').required = true;
            this.skuAndSerial.querySelector('input[name="Item_SKU_Model_Number"]').value = '';
          }
        }
      });
    });

    // Hidden Radio (Pen) Selection
    this.querySelectorAll('.hidden-radio').forEach((pen) => {
      pen.addEventListener('change', (e) => {
        if (e.target.checked) {
          if (this.skuAndSerial) this.skuAndSerial.querySelector('input[name="Item_SKU_Model_Number"]').value = e.target.value;
          if (this.errorMessage && this.penSelection.contains(this.errorMessage)) {
            this.penSelection.removeChild(this.errorMessage);
            this.errorMessage = null;
            const group = this.penSelection.querySelector('[role="radiogroup"]');
            if (group) group.removeAttribute('aria-describedby');
          }
        }
      });
    });

    // Form Submission
    this.form.addEventListener('submit', (e) => {
      if (sessionStorage.getItem('submitted')) {
        e.preventDefault();
        return;
      }

      e.preventDefault();

      // Clear previous messages
      this.form.querySelectorAll('.error-message').forEach((el) => el.remove());
      if (this.errorMessage && this.penSelection.contains(this.errorMessage)) {
        this.penSelection.removeChild(this.errorMessage);
        this.errorMessage = null;
      }

      const selectedProductTypeRadio = this.querySelector('input[name="productType"]:checked');
      const selectedProductType = selectedProductTypeRadio ? selectedProductTypeRadio.value : '';

      // Custom validation for pen selection
      if (selectedProductType === 'pen') {
        const selectedPen = this.querySelector('.hidden-radio:checked');
        if (!selectedPen) {
          this.errorMessage = document.createElement('div');
          this.errorMessage.className = 'error-message text-red-500 font-body text-sm mt-2';
          this.errorMessage.id = 'RegisterPenError';
          this.errorMessage.setAttribute('role', 'alert');
          this.errorMessage.textContent = 'Please select a pen.';
          if (this.penSelection) {
            this.penSelection.appendChild(this.errorMessage);
            const group = this.penSelection.querySelector('[role="radiogroup"]');
            if (group) group.setAttribute('aria-describedby', 'RegisterPenError');
            this.penSelection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            const firstPen = this.penSelection.querySelector('.hidden-radio');
            if (firstPen) firstPen.focus({ preventScroll: true });
          }
          return;
        }
      }

      // Validate form
      if (!this.form.reportValidity()) {
        return;
      }

      // Disable submit button
      const originalButtonHtml = this.submitButton.innerHTML;
      this.submitButton.disabled = true;
      this.submitButton.innerHTML = '<span class="font-subheading text-[14px] font-medium uppercase tracking-[1px] text-white">Submitting...</span>';

      // Get form field values
      const formData = new FormData(this.form);
      const getFieldValue = (name) => {
        const value = formData.get(name);
        return value ? value.trim() : '';
      };

      // Get SKU - either from pen selection or manual input
      let sku = '';
      if (selectedProductType === 'pen') {
        const selectedPen = this.querySelector('.hidden-radio:checked');
        sku = selectedPen ? selectedPen.value : '';
      } else {
        sku = getFieldValue('Item_SKU_Model_Number');
      }

      // Build the warranty registration payload
      const subscribeCheckbox = this.form.querySelector('#subscribeToMarketing');

      const warrantyData = {
        shopId: this.shopId,
        customer: {
          firstName: getFieldValue('FirstName'),
          lastName: getFieldValue('LastName'),
          email: getFieldValue('Email'),
          gender: RegisterProductForm.mapGender(getFieldValue('Gender')),
          birthday: getFieldValue('Birthday'),
          averageIncome: RegisterProductForm.mapAverageIncome(getFieldValue('Average_Income')),
          phone: getFieldValue('Phone'),
          subscribeToMarketing: subscribeCheckbox ? subscribeCheckbox.checked : true,
          address: {
            address1: getFieldValue('Street'),
            address2: '',
            city: getFieldValue('City'),
            state: getFieldValue('State'),
            postalCode: getFieldValue('PostalCode'),
            country: (() => {
              const countrySelect = document.getElementById('country');
              if (countrySelect && countrySelect.selectedIndex > 0) {
                return countrySelect.options[countrySelect.selectedIndex].text;
              }
              return getFieldValue('Country');
            })(),
          },
        },
        product: {
          sku: sku,
          productType: selectedProductType,
          serialNumber: getFieldValue('Serial_Number'),
          placeOfPurchase: getFieldValue('Place_of_Purchase'),
          dateOfPurchase: getFieldValue('Date_of_Purchase'),
        },
      };

      // Send data to warranty API
      fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(warrantyData),
      })
        .then((response) => {
          if (!response.ok) {
            return response.text().then((text) => {
              let errorText = text;
              try {
                const errorJson = JSON.parse(text);
                errorText = errorJson.message || errorJson.error || text;
              } catch (e) {
                // Use text as is if not JSON
              }
              throw new Error(errorText || `Network response was not ok: ${response.status} ${response.statusText}`);
            });
          }
          return response.json();
        })
        .then((data) => {
          sessionStorage.setItem('submitted', 'true');
          this.showModal(this.successMessage, () => {
            window.location.href = this.successUrl;
            sessionStorage.removeItem('submitted');
          });
        })
        .catch((error) => {
          const errorMsg = error.message || '';

          // If the backend rejected the province and we have a locality fallback, retry silently
          if (errorMsg.includes('Province is invalid') && this.lastLocality) {
            warrantyData.customer.address.state = this.lastLocality;

            fetch(this.apiUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(warrantyData),
            })
              .then((response) => {
                if (!response.ok) {
                  return response.text().then((text) => {
                    let errorText = text;
                    try {
                      const errorJson = JSON.parse(text);
                      errorText = errorJson.message || errorJson.error || text;
                    } catch (e) {
                      // Use text as is if not JSON
                    }
                    throw new Error(errorText || `Network response was not ok: ${response.status} ${response.statusText}`);
                  });
                }
                return response.json();
              })
              .then((data) => {
                sessionStorage.setItem('submitted', 'true');
                this.showModal(this.successMessage, () => {
                  window.location.href = this.successUrl;
                  sessionStorage.removeItem('submitted');
                });
              })
              .catch((retryError) => {
                // Re-enable submit button only after retry also fails
                this.submitButton.disabled = false;
                this.submitButton.innerHTML = originalButtonHtml;
                this.showModal(retryError.message || errorMsg);
              });
          } else {
            // Re-enable submit button
            this.submitButton.disabled = false;
            this.submitButton.innerHTML = originalButtonHtml;
            this.showModal(errorMsg);
          }
        });
    });
  }

  showModal(message, callback) {
    const overlay = document.getElementById('rp-modal');
    const messageEl = document.getElementById('rp-modal-message');
    const okBtn = document.getElementById('rp-modal-ok');
    if (!overlay || !messageEl || !okBtn) return;

    messageEl.textContent = message;
    overlay.classList.add('is-open');
    document.body.classList.add('rp-modal-open');

    // Accessibility: move focus into the dialog so the message is announced,
    // keep Tab on the OK button, close with Escape and return focus afterwards.
    const returnFocusTo = document.activeElement instanceof HTMLElement && document.activeElement !== document.body
      ? document.activeElement
      : this.submitButton;
    okBtn.focus();

    const close = () => {
      overlay.classList.remove('is-open');
      document.body.classList.remove('rp-modal-open');
      overlay.removeEventListener('click', onOverlayClick);
      overlay.removeEventListener('keydown', onKeydown);
      okBtn.removeEventListener('click', onOkClick);
      if (typeof callback === 'function') {
        callback();
      } else if (returnFocusTo && returnFocusTo.isConnected) {
        returnFocusTo.focus();
      }
    };

    const onOkClick = () => close();
    const onOverlayClick = (e) => {
      if (e.target === overlay) close();
    };
    const onKeydown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === 'Tab') {
        e.preventDefault();
        okBtn.focus();
      }
    };

    okBtn.addEventListener('click', onOkClick, { once: true });
    overlay.addEventListener('click', onOverlayClick);
    overlay.addEventListener('keydown', onKeydown);
  }

  // Google Maps Methods
  loadGoogleMapsScript(callback) {
    if (document.getElementById('googleMapsScript')) {
      if (window.google && window.google.maps && window.google.maps.places) {
        callback();
      }
      return;
    }

    if (!this.googleMapsApiKey) return;

    const script = document.createElement('script');
    script.id = 'googleMapsScript';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.googleMapsLoaded = true;
      this.autocompleteService = new window.google.maps.places.AutocompleteService();
      if (callback) callback();
    };
    script.onerror = function () {
      console.error('Failed to load Google Maps script');
    };
    document.head.appendChild(script);
  }

  getAddressSuggestions(input, inputType) {
    if (!this.autocompleteService || !input.value.trim()) {
      this.hideSuggestions(inputType);
      return;
    }

    const countrySelect = document.getElementById('country');
    if (!countrySelect) {
      this.hideSuggestions(inputType);
      return;
    }

    const countryCode = countrySelect.value;
    if (!countryCode || countryCode.trim() === '') {
      this.hideSuggestions(inputType);
      return;
    }

    const request = {
      input: input.value,
      componentRestrictions: { country: countryCode },
    };

    if (inputType === 'street') {
      request.types = ['address'];
    } else if (inputType === 'city') {
      request.types = ['(cities)'];
    }

    this.autocompleteService.getPlacePredictions(request, (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        this.showSuggestions(predictions, inputType);
      } else {
        this.hideSuggestions(inputType);
      }
    });
  }

  showSuggestions(predictions, inputType) {
    const suggestionsContainer = document.getElementById(`${inputType}-suggestions`);
    if (!suggestionsContainer) return;

    suggestionsContainer.innerHTML = '';
    predictions.forEach((prediction, index) => {
      const item = document.createElement('div');
      item.className = 'address-suggestion-item font-body text-[14px] text-black';
      item.textContent = prediction.description;
      item.setAttribute('data-place-id', prediction.place_id);
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      item.id = `${inputType}-suggestion-${index}`;

      item.addEventListener('click', () => {
        this.selectSuggestion(prediction.description, inputType);
      });

      item.addEventListener('mouseenter', function () {
        document.querySelectorAll(`#${inputType}-suggestions .address-suggestion-item`).forEach((el) => {
          el.classList.remove('active');
        });
        this.classList.add('active');
      });

      suggestionsContainer.appendChild(item);
    });

    suggestionsContainer.style.display = 'block';
    this.activeSuggestions = suggestionsContainer;
    this.activeInput = inputType;
    this.setExpanded(inputType, true);
  }

  // Combobox state for the address inputs (WAI-ARIA combobox pattern).
  setExpanded(inputType, expanded) {
    const input = document.getElementById(inputType);
    if (!input) return;
    input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    if (!expanded) input.removeAttribute('aria-activedescendant');
  }

  setActiveOption(items, index) {
    items.forEach((item, i) => {
      const active = i === index;
      item.classList.toggle('active', active);
      item.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    const input = document.getElementById(this.activeInput);
    if (input && items[index]) input.setAttribute('aria-activedescendant', items[index].id);
  }

  hideSuggestions(inputType) {
    const suggestionsContainer = document.getElementById(`${inputType}-suggestions`);
    if (suggestionsContainer) {
      suggestionsContainer.style.display = 'none';
    }
    this.setExpanded(inputType, false);
    if (this.activeInput === inputType) {
      this.activeSuggestions = null;
      this.activeInput = null;
    }
  }

  selectSuggestion(description, inputType) {
    if (!this.googleMapsApiKey) return;

    const countrySelect = document.getElementById('country');
    const countryCode = countrySelect ? countrySelect.value : null;
    const geocodeUrl = countryCode
      ? `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          description
        )}&components=country:${countryCode}&key=${this.googleMapsApiKey}`
      : `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(description)}&key=${
          this.googleMapsApiKey
        }`;

    fetch(geocodeUrl)
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 'OK' && data.results && data.results.length > 0) {
          const addressDetails = data.results[0];
          const addressComponents = {};

          addressDetails.address_components.forEach((component) => {
            component.types.forEach((type) => {
              addressComponents[type] = component.long_name;
            });
          });

          // Store locality for province fallback (used if backend rejects administrative_area_level_1)
          this.lastLocality = addressComponents.locality || null;

          const streetInput = document.getElementById('street');
          const cityInput = document.getElementById('city');
          const stateInput = document.getElementById('state');
          const zipInput = document.getElementById('zip');
          const countryInput = document.getElementById('country');

          const streetNumber = addressComponents.street_number || '';
          const route = addressComponents.route || '';
          const streetAddress = streetNumber ? `${streetNumber} ${route}`.trim() : route;

          if (streetInput && streetAddress) {
            streetInput.value = streetAddress;
          }

          if (cityInput) {
            cityInput.value =
              addressComponents.locality || addressComponents.postal_town || addressComponents.sublocality || '';
          }

          if (stateInput) {
            stateInput.value = addressComponents.administrative_area_level_1 || '';
          }

          if (zipInput) {
            zipInput.value = addressComponents.postal_code || '';
          }

          if (countryInput && addressComponents.country) {
            const countryName = addressComponents.country;
            const options = countryInput.options;
            let found = false;

            for (let i = 0; i < options.length; i++) {
              if (options[i].text.trim() === countryName.trim()) {
                countryInput.value = options[i].value;
                found = true;
                break;
              }
            }

            if (!found) {
              for (let i = 0; i < options.length; i++) {
                const optionText = options[i].text.trim().toLowerCase();
                const googleCountry = countryName.trim().toLowerCase();
                if (optionText.includes(googleCountry) || googleCountry.includes(optionText)) {
                  countryInput.value = options[i].value;
                  break;
                }
              }
            }
          }

          this.hideSuggestions(inputType);
        }
      })
      .catch(() => {});
  }

  initializeAutocomplete() {
    if (!this.googleMapsApiKey) return;

    const streetInput = document.getElementById('street');
    const cityInput = document.getElementById('city');
    const stateInput = document.getElementById('state');
    const countrySelect = document.getElementById('country');

    [streetInput, cityInput, stateInput].forEach((input) => {
      if (!input) return;
      const list = document.getElementById(`${input.id}-suggestions`);
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-expanded', 'false');
      if (list) {
        list.setAttribute('role', 'listbox');
        list.setAttribute('aria-label', `${input.id} suggestions`);
        input.setAttribute('aria-controls', list.id);
      }
    });

    // Country change listener - clear suggestions when country changes
    if (countrySelect) {
      countrySelect.addEventListener('change', () => {
        this.hideSuggestions('street');
        this.hideSuggestions('city');
        this.hideSuggestions('state');

        if (streetInput && streetInput.value.trim() && this.googleMapsLoaded && this.autocompleteService) {
          this.getAddressSuggestions(streetInput, 'street');
        } else if (cityInput && cityInput.value.trim() && this.googleMapsLoaded && this.autocompleteService) {
          this.getAddressSuggestions(cityInput, 'city');
        } else if (stateInput && stateInput.value.trim() && this.googleMapsLoaded && this.autocompleteService) {
          this.getAddressSuggestions(stateInput, 'state');
        }
      });
    }

    // Street input autocomplete
    if (streetInput) {
      let streetTimeout;
      streetInput.addEventListener('input', () => {
        clearTimeout(streetTimeout);
        streetTimeout = setTimeout(() => {
          if (this.googleMapsLoaded && this.autocompleteService) {
            this.getAddressSuggestions(streetInput, 'street');
          }
        }, 300);
      });

      streetInput.addEventListener('blur', () => {
        setTimeout(() => this.hideSuggestions('street'), 200);
      });

      streetInput.addEventListener('focus', () => {
        if (streetInput.value.trim() && this.googleMapsLoaded && this.autocompleteService) {
          this.getAddressSuggestions(streetInput, 'street');
        }
      });
    }

    // City input autocomplete
    if (cityInput) {
      let cityTimeout;
      cityInput.addEventListener('input', () => {
        clearTimeout(cityTimeout);
        cityTimeout = setTimeout(() => {
          if (this.googleMapsLoaded && this.autocompleteService) {
            this.getAddressSuggestions(cityInput, 'city');
          }
        }, 300);
      });

      cityInput.addEventListener('blur', () => {
        setTimeout(() => this.hideSuggestions('city'), 200);
      });

      cityInput.addEventListener('focus', () => {
        if (cityInput.value.trim() && this.googleMapsLoaded && this.autocompleteService) {
          this.getAddressSuggestions(cityInput, 'city');
        }
      });
    }

    // State input autocomplete
    if (stateInput) {
      let stateTimeout;
      stateInput.addEventListener('input', () => {
        clearTimeout(stateTimeout);
        stateTimeout = setTimeout(() => {
          if (this.googleMapsLoaded && this.autocompleteService) {
            this.getAddressSuggestions(stateInput, 'state');
          }
        }, 300);
      });

      stateInput.addEventListener('blur', () => {
        setTimeout(() => this.hideSuggestions('state'), 200);
      });

      stateInput.addEventListener('focus', () => {
        if (stateInput.value.trim() && this.googleMapsLoaded && this.autocompleteService) {
          this.getAddressSuggestions(stateInput, 'state');
        }
      });
    }

    // Close suggestions when clicking outside
    document.addEventListener('click', (e) => {
      if (
        this.activeSuggestions &&
        !this.activeSuggestions.contains(e.target) &&
        e.target.id !== 'street' &&
        e.target.id !== 'city' &&
        e.target.id !== 'state'
      ) {
        this.hideSuggestions(this.activeInput);
      }
    });

    // Keyboard navigation for suggestions
    document.addEventListener('keydown', (e) => {
      if (!this.activeSuggestions || this.activeSuggestions.style.display === 'none') return;

      const items = this.activeSuggestions.querySelectorAll('.address-suggestion-item');
      if (items.length === 0) return;
      const activeItem = this.activeSuggestions.querySelector('.address-suggestion-item.active');
      let currentIndex = Array.from(items).indexOf(activeItem);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        currentIndex = (currentIndex + 1) % items.length;
        this.setActiveOption(Array.from(items), currentIndex);
        items[currentIndex].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        currentIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
        this.setActiveOption(Array.from(items), currentIndex);
        items[currentIndex].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && activeItem) {
        e.preventDefault();
        activeItem.click();
      } else if (e.key === 'Escape') {
        this.hideSuggestions(this.activeInput);
      }
    });
  }
}

customElements.define('register-product-form', RegisterProductForm);
